import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, Select, MenuItem, FormControl, InputLabel, CircularProgress, FormControlLabel, Switch } from "@mui/material";
import { CalendarMonthOutlined, MenuBookOutlined, WarningAmber } from "@mui/icons-material";
import SavingOverlay from "../components/SavingOverlay";
import { aiIntegrationService, AssistantAgentOption, AssistantAreaSetting } from "../services/aiIntegrationService";
import { voiceService, VoiceSettings } from "../services/voiceService";
import { colors, typography } from "../theme/designSystem";

interface Props {
  entityId: string;
  onToast: (message: string, type: "success" | "error") => void;
}

interface AreaDef {
  key: string;
  name: string;
  icon: React.ReactNode;
  description: React.ReactNode;
  /** Agente de alteração: só na área que tem ferramentas de propor (Agenda e Livro-caixa). */
  writer: boolean;
  /** Texto do botão: a base de conhecimento cita o da Agenda. */
  saveLabel: string;
  /** Modo conversa (RN-570): só na área com resposta falada. */
  speech: boolean;
}

const AREAS: AreaDef[] = [
  {
    key: "agenda",
    name: "Agenda",
    icon: <CalendarMonthOutlined sx={{ color: colors.primary }} />,
    description: (
      <>
        O <strong>agente de consulta</strong> responde sobre a agenda na área Agenda da página Super Assistente. O{" "}
        <strong>agente de alteração</strong> prepara inclusões, remarcações e inativações de sessões avulsas, e nada é
        gravado sem a pessoa confirmar.
      </>
    ),
    writer: true,
    saveLabel: "Salvar configuração da Agenda",
    speech: true,
  },
  {
    key: "livro-caixa",
    name: "Livro-caixa",
    icon: <MenuBookOutlined sx={{ color: colors.primary }} />,
    description: (
      <>
        O <strong>agente de consulta</strong> atende a área Livro-caixa da página Super Assistente, onde a pessoa envia o extrato
        do banco e vê o que já está no livro e o que falta lançar. O <strong>agente de alteração</strong> prepara
        lançamentos, baixas, quitações, correções e regras, e nada é gravado sem a pessoa confirmar. A área aparece
        para quem também tem a tela do Livro-caixa.
      </>
    ),
    writer: true,
    saveLabel: "Salvar configuração do Livro-caixa",
    speech: true,
  },
];

const Warning: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, backgroundColor: "#fff3cd", border: "1px solid #ffe69c", borderRadius: "8px", p: "0.75rem 1rem", mb: 1.5, maxWidth: "56rem" }}>
    <WarningAmber sx={{ color: "#856404", fontSize: 20, mt: "2px" }} />
    <Typography variant="body2" sx={{ color: "#856404", fontSize: typography.fontSize.sm }}>{children}</Typography>
  </Box>
);

/**
 * Agentes de uma área da página Super Assistente (RN-565): o de consulta atende a conversa; o de alteração propõe,
 * sempre com confirmação. Agentes diferentes de propósito (D-OP-14): consulta para muitos, alteração para poucos.
 */
const AreaBlock: React.FC<{
  def: AreaDef;
  entityId: string;
  agents: AssistantAgentOption[];
  setting: AssistantAreaSetting | undefined;
  voice: VoiceSettings | null;
  onToast: Props["onToast"];
}> = ({ def, entityId, agents, setting, voice, onToast }) => {
  const [readId, setReadId] = useState(setting?.readAgentId || "");
  const [writeId, setWriteId] = useState(setting?.writeAgentId || "");
  const [speech, setSpeech] = useState(!!setting?.speechReply);
  const [saved, setSaved] = useState({ read: setting?.readAgentId || "", write: setting?.writeAgentId || "", speech: !!setting?.speechReply });
  const [saving, setSaving] = useState(false);

  const reader = useMemo(() => agents.find((a) => a.id === readId), [agents, readId]);
  const writer = useMemo(() => agents.find((a) => a.id === writeId), [agents, writeId]);
  const dirty = readId !== saved.read || writeId !== saved.write || speech !== saved.speech;
  const id = `assistant-${def.key}`;

  const handleReadChange = (value: string) => {
    setReadId(value);
    // Sem consulta não há alteração nem voz; e o mesmo agente não ocupa os dois papéis
    if (!value || value === writeId) setWriteId("");
    if (!value) setSpeech(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await aiIntegrationService.saveAssistantArea(entityId, def.key, readId || null, writeId || null, speech);
      setSaved({ read: readId, write: writeId, speech });
      onToast(readId ? `Configuração da área ${def.name} salva.` : `Área ${def.name} desligada.`, "success");
    } catch (err: any) {
      onToast(err?.response?.data?.description || `Não foi possível salvar os agentes da área ${def.name}.`, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #e9ecef" }}>
      <SavingOverlay open={saving} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        {def.icon}
        <Typography sx={{ fontWeight: 600, color: colors.textPrimary }}>Super Assistente · {def.name}</Typography>
      </Box>
      <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2, maxWidth: "56rem" }}>
        {def.description} Cada agente vale só para quem o tiver liberado em <strong>Tecnologia &gt; Agentes de IA &gt; Usuários</strong>.
        Deixe a consulta em branco para desligar a área.
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ flex: "1 1 16rem", maxWidth: { sm: "22rem" } }}>
          <InputLabel id={`${id}-read`}>Agente de consulta</InputLabel>
          <Select labelId={`${id}-read`} label="Agente de consulta" value={readId} onChange={(e) => handleReadChange(e.target.value)}>
            <MenuItem value=""><em>Desligado</em></MenuItem>
            {agents.map((a) => <MenuItem key={a.id} value={a.id}>{a.displayName || "Agente sem nome"}</MenuItem>)}
          </Select>
        </FormControl>
        {def.writer && (
          <FormControl size="small" disabled={!readId} sx={{ flex: "1 1 16rem", maxWidth: { sm: "22rem" } }}>
            <InputLabel id={`${id}-write`}>Agente de alteração</InputLabel>
            <Select labelId={`${id}-write`} label="Agente de alteração" value={writeId} onChange={(e) => setWriteId(e.target.value)}>
              <MenuItem value=""><em>Só consulta</em></MenuItem>
              {agents.filter((a) => a.id !== readId).map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.displayName || "Agente sem nome"}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {agents.length === 0 && (
        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
          Nenhum agente ativo. Crie os agentes em Tecnologia &gt; Agentes de IA.
        </Typography>
      )}

      {reader && reader.assignedUsers === 0 && (
        <Warning>
          <strong>{reader.displayName}</strong> não está liberado para ninguém; sem isso a área {def.name} não aparece para
          ninguém.
        </Warning>
      )}
      {writer && writer.assignedUsers === 0 && (
        <Warning>
          <strong>{writer.displayName}</strong> não está liberado para ninguém; a área fica só com consulta.
        </Warning>
      )}
      {writer && writer.showInChat && (
        <Warning>
          <strong>{writer.displayName}</strong> está com <strong>Aparece no chat</strong> ligado. Desligue no cadastro do
          agente (aba Ferramentas): o agente de alteração atende só pela área {def.name}.
        </Warning>
      )}

      {def.speech && voice && (
        <Box sx={{ mb: 2, maxWidth: "56rem" }}>
          <FormControlLabel
            control={<Switch checked={speech} disabled={saving || !readId || (!voice.speechReady && !speech)}
              onChange={(e) => setSpeech(e.target.checked)} />}
            label={<Typography sx={{ fontSize: typography.fontSize.sm, fontWeight: 600 }}>Modo conversa (resposta falada)</Typography>}
          />
          <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: typography.fontSize.sm }}>
            Libera o botão de modo conversa na área {def.name}: a pessoa fala, o assistente responde em voz e volta a ouvir.
            A voz é gerada no servidor da clínica, o texto não sai dele e o áudio não é guardado. Cada frase leva alguns
            segundos para ser gerada. Confirmar uma alteração continua exigindo o clique.
          </Typography>
          {!voice.speechReady && (
            <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: typography.fontSize.sm, mt: 0.5 }}>
              A voz ainda não está disponível neste ambiente.
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ mb: 1 }}>
        <Button onClick={handleSave} disabled={saving || !dirty} variant="contained" sx={{ textTransform: "none", backgroundColor: colors.primary }}>
          {def.saveLabel}
        </Button>
      </Box>
    </Box>
  );
};

const AssistantAreasConfig: React.FC<Props> = ({ entityId, onToast }) => {
  const [agents, setAgents] = useState<AssistantAgentOption[]>([]);
  const [settings, setSettings] = useState<AssistantAreaSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [voice, setVoice] = useState<VoiceSettings | null>(null);

  useEffect(() => {
    let alive = true;
    voiceService.getSettings().then((v) => { if (alive) setVoice(v); }).catch(() => undefined);
    return () => { alive = false; };
  }, [entityId]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    aiIntegrationService
      .getAssistantAreas(entityId)
      .then((data) => {
        if (!alive) return;
        setAgents(data.agents);
        setSettings(data.areas);
      })
      .catch(() => { if (alive) onToast("Não foi possível carregar os agentes dos Assistentes.", "error"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [entityId, onToast]);

  if (loading) {
    return <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}><CircularProgress size={22} /></Box>;
  }

  return (
    <>
      {AREAS.map((def) => (
        <AreaBlock key={def.key} def={def} entityId={entityId} agents={agents} voice={voice} onToast={onToast}
          setting={settings.find((s) => s.areaKey === def.key)} />
      ))}
    </>
  );
};

export default AssistantAreasConfig;
